import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useCognito } from '../../hooks/auth/useCognito';
import { useAuthForms } from '../../hooks/auth/useAuthForms';
import { useAuth } from '../../context/authcontext';
import { ViewState, UserSession } from '../../types/auth';
import { useMobileDetection } from '../../hooks/useMobileUtils';

// Import components
import SignInForm from './forms/SignInForm';
import SignUpForm from './forms/SignUpForm';
import EmailVerification from './forms/EmailVerification';
import ForgotPassword from './forms/ForgotPassword';
import NewPasswordForm from './forms/NewPasswordForm';
import MfaSetup from './mfa/MfaSetup';
import MfaVerify from './mfa/MfaVerify';
import InstallAppButton from './InstallAppButton';

// Import services
import {
  signUp,
  verifyEmail,
  resendVerificationCode,
  forgotPassword,
  confirmForgotPassword,
  setupMFA,
  verifyMFA,
  saveSession,
  getUserAttributesFromToken
} from '../../services/auth/cognito.service';
import {
  AuthProxyService,
  AuthProxyError,
  AUTH_MESSAGES,
  AuthProxyResult,
  AuthProxyTokens,
  hasTokens
} from '../../services/auth/authProxy.service';
import { isPasswordValid } from '../../utils/passwordPolicy';
import { isSandboxBuild, SANDBOX_SIGNUP_MESSAGES, sandboxSignupError } from '../../utils/sandbox';

// Static image import - full white logo used on signin banner
import iconWhite from '../../assets/images/icon_white.png';

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const isSessionExpired = (err: unknown): boolean =>
  (err instanceof AuthProxyError && err.code === 'SESSION_EXPIRED') ||
  (err instanceof Error && err.message.toLowerCase().includes('session'));

interface AuthenticationProps {
  region?: string;
  userPoolId?: string;
  clientId?: string;
  onSignedIn?: () => void;
}

const AuthenticationComponent: React.FC<AuthenticationProps> = ({
  region = 'us-east-1',
  userPoolId,
  clientId,
  onSignedIn
}) => {
  const navigate = useNavigate();
  const { setUser, loadPermissions, isLoading: permissionsLoading } = useAuth();
  const isMobile = useMobileDetection();

  // Initialize the Cognito client and related state
  const {
    cognitoClient,
    orgId,
    setOrgId,
    poolId,
    appClientId
  } = useCognito(region, userPoolId, clientId);

  // Initialize form data and handlers
  const {
    signinData,
    signupData,
    handleSigninChange,
    handleSignupChange,
    setSigninEmail
  } = useAuthForms();

  // Auth flow state
  const [view, setViewState] = useState<ViewState>('signin');
  const [error, setError] = useState<string | null>(null);
  // Success message for the sign-in view (email verified, password reset, ...)
  const [notice, setNotice] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Sign-in CAPTCHA (Phase 5, 2026-10-07): shown once /auth/login says it is required (3+ failures).
  // Turnstile tokens are single-use, so the widget is reset after every attempt.
  const [captchaRequired, setCaptchaRequired] = useState<boolean>(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaResetKey, setCaptchaResetKey] = useState<number>(0);
  // Demo sandbox sign-up CAPTCHA (demo-sandbox plan §4.1): checked by the PreSignUp trigger
  const [signupCaptchaToken, setSignupCaptchaToken] = useState<string | null>(null);
  const [signupCaptchaResetKey, setSignupCaptchaResetKey] = useState<number>(0);
  const [waitingForPermissions, setWaitingForPermissions] = useState<boolean>(false);
  // username: what /auth/challenge expects back (the backend returns the Cognito sub for challenges).
  // email: what the user typed; used for display, the MFA QR label, prefill_email and the session.
  const [currentUser, setCurrentUser] = useState<{
    username: string;
    email: string;
    session?: string;
  } | null>(null);
  const [, setSession] = useState<UserSession | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [resetPasswordEmail, setResetPasswordEmail] = useState<string>('');

  /** Every view change starts clean: no error or notice carried over from the previous view. */
  const setView = (next: ViewState) => {
    setError(null);
    setNotice(null);
    setViewState(next);
  };

  /** Back to sign-in with the email filled in and an optional success notice. */
  const goToSignIn = (email?: string, successNotice?: string) => {
    setView('signin');
    setCurrentUser(null);
    if (email) setSigninEmail(email);
    if (successNotice) setNotice(successNotice);
  };
  // Phase 12: the family portal accept page sends guardians here with the email filled in
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { prefillEmail?: unknown; notice?: unknown } | null;
    if (state && typeof state.prefillEmail === 'string' && state.prefillEmail) {
      setSigninEmail(state.prefillEmail);
      if (typeof state.notice === 'string' && state.notice) setNotice(state.notice);
    }
    // Only on arrival; setSigninEmail is recreated each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Set body style for full height and proper scrolling
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.minHeight = '100vh';
    document.body.style.overflowX = 'hidden';
    document.body.style.overflowY = 'auto';
    // Add a min-height to html and body to ensure proper display and scrolling
    document.documentElement.style.minHeight = '100%';
    document.documentElement.style.height = '100%';
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.minHeight = '';
      document.body.style.overflowX = '';
      document.body.style.overflowY = '';
      document.body.style.overflow = '';
      document.documentElement.style.minHeight = '';
      document.documentElement.style.height = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  /**
   * Wait for permissions to load after signin, then navigate to dashboard.
   * App.tsx gates the dashboard on MFA enrollment, pending legal acceptance and the BAA.
   */
  useEffect(() => {
    if (waitingForPermissions && !permissionsLoading) {
      // NOTE: Do NOT set permissionsLoaded here - PermissionsInitializer in App.tsx
      // handles this after the async loadPermissions completes.
      setWaitingForPermissions(false);
      navigate('/dashboard');
    }
  }, [waitingForPermissions, permissionsLoading, navigate]);

  /**
   * Send the user back to sign-in after a short delay (expired challenge session),
   * keeping the message that explains why.
   */
  const returnToSignIn = (email?: string, delay = 2000) => {
    setTimeout(() => {
      setViewState('signin');
      setCurrentUser(null);
      if (email) setSigninEmail(email);
    }, delay);
  };

  /**
   * Sign-in finished: keep tokens (access/ID in memory, refresh in sessionStorage),
   * set the user from the ID token claims and load permissions.
   * SECURITY: tokens and claims are never logged.
   */
  const completeSignIn = (tokens: AuthProxyTokens, email: string) => {
    const userSession: UserSession = {
      idToken: tokens.idToken || '',
      accessToken: tokens.accessToken || '',
      refreshToken: tokens.refreshToken || '',
      username: email
    };
    setSession(userSession);
    saveSession(userSession);

    const attrs = getUserAttributesFromToken(userSession.idToken);
    setUser({
      id: attrs?.sub || '',
      email: attrs?.email || email,
      firstName: attrs?.firstName || '',
      lastName: attrs?.lastName || '',
      tenantId: attrs?.tenantId || '',
      providerType: attrs?.providerType,
      userType: attrs?.userType,
      groups: attrs?.groups || []
    });

    // Sets isAuthenticated in App
    if (onSignedIn) {
      onSignedIn();
    }

    // The waitingForPermissions effect navigates once permissions finish loading
    setWaitingForPermissions(true);
    if (!permissionsLoading) {
      loadPermissions();
    }
  };

  /** MFA_SETUP challenge: associate a TOTP secret with the challenge session and show the QR code. */
  const startMfaSetup = async (username: string, email: string, challengeSession: string) => {
    setCurrentUser({ username, email, session: challengeSession });
    try {
      const mfaSetupResult = await setupMFA({
        cognitoClient,
        session: challengeSession,
        // QR label: the email, not the Cognito sub the challenge carries
        username: email
      });
      // AssociateSoftwareToken consumed the original session
      setCurrentUser({ username, email, session: mfaSetupResult.session || challengeSession });
      setQrCodeUrl(mfaSetupResult.qrCodeUrl);
      setView('mfa-setup');
    } catch (setupErr) {
      console.error('Error during MFA setup');
      setError(isSessionExpired(setupErr)
        ? 'Your session has expired. Please sign in again to set up MFA.'
        : 'Failed to set up MFA. Please try signing in again.');
      returnToSignIn(email);
    }
  };

  /**
   * Route a /auth/login or /auth/challenge result to the next step.
   * @param email the email the user typed (display, QR label, session)
   * @param challengeUsername the username from the previous challenge, if any
   */
  const handleAuthResult = async (result: AuthProxyResult, email: string, challengeUsername?: string): Promise<void> => {
    if (hasTokens(result)) {
      completeSignIn(result.tokens, email);
      return;
    }
    // The backend returns the Cognito sub here; it goes back to /auth/challenge unchanged
    const username = result.username || challengeUsername || email;
    switch (result.challengeName) {
      case 'SOFTWARE_TOKEN_MFA':
        setCurrentUser({ username, email, session: result.session });
        setView('mfa-verify');
        return;
      case 'NEW_PASSWORD_REQUIRED':
        setCurrentUser({ username, email, session: result.session });
        setView('new-password');
        return;
      case 'MFA_SETUP':
        await startMfaSetup(username, email, result.session);
        return;
      case 'SELECT_MFA_TYPE': {
        // TOTP is the only MFA type offered
        const next = await AuthProxyService.respond({
          username,
          challengeName: 'SELECT_MFA_TYPE',
          session: result.session,
          responses: { ANSWER: 'SOFTWARE_TOKEN_MFA' }
        });
        await handleAuthResult(next, email, username);
        return;
      }
      default:
        throw new AuthProxyError('UNKNOWN');
    }
  };

  /**
   * Sign In Handler: POST /auth/login through the login proxy (Phase 5, ACC-06).
   */
  const handleSignin = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent): Promise<void> => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);
    setNotice(null);
    const email = signinData.email.trim();

    // Basic validation
    if (!email || !signinData.password) {
      setError('Please enter both email and password');
      return;
    }

    // The Cognito client is still needed for MFA setup, sign-up and password reset
    if (!cognitoClient || (!poolId && !orgId)) {
      setError('Authentication service not configured correctly');
      return;
    }

    if (captchaRequired && !captchaToken) {
      setError(AUTH_MESSAGES.CAPTCHA_REQUIRED);
      return;
    }

    setIsLoading(true);
    const usedToken = captchaRequired ? captchaToken : null;

    try {
      const result = await AuthProxyService.login(email, signinData.password, usedToken ?? undefined);
      await handleAuthResult(result, email);
    } catch (err) {
      if (err instanceof AuthProxyError && err.captchaRequired) setCaptchaRequired(true);
      if (err instanceof AuthProxyError && err.code === 'USER_NOT_CONFIRMED') {
        // Sign-up never confirmed: send a fresh code (the first one may have expired), then verify
        setCurrentUser({ username: email, email });
        setVerificationCode('');
        setView('verify-email');
        try {
          await resendVerificationCode(cognitoClient, email, appClientId || orgId || '');
          setError(err.message);
        } catch (resendErr) {
          setError(errorMessage(resendErr, err.message));
        }
      } else if (err instanceof AuthProxyError && err.code === 'PASSWORD_RESET_REQUIRED') {
        // An administrator required a reset: go straight to the reset form
        setResetPasswordEmail(email);
        setView('forgot-password');
        setError(err.message);
      } else {
        console.error('Sign in failed:', err instanceof AuthProxyError ? err.code : 'UNKNOWN');
        setError(errorMessage(err, 'Failed to sign in'));
      }
    } finally {
      setIsLoading(false);
      if (usedToken) {
        // The token was spent: clear it and get a new one from the widget
        setCaptchaToken(null);
        setCaptchaResetKey((key) => key + 1);
      }
    }
  };
    /**
   * Sign Up Handler
   */
  const handleSignup = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent): Promise<void> => {
    if (e && e.preventDefault) e.preventDefault();
    setError(null);

    const email = signupData.email.trim();
    const organizationName = signupData.organizationName.trim();

    // Basic validation. NFR-09 (Phase 5): sign-up is open to every state; the state is optional.
    // The organization name is required: the PostConfirmation trigger cannot create the tenant
    // without it (Cognito drops an empty attribute), which would leave a confirmed user with no account.
    if (!email || !signupData.password || !signupData.firstName.trim() || !signupData.lastName.trim() || !organizationName) {
      setError('Please fill out all required fields');
      return;
    }

    if (!isPasswordValid(signupData.password)) {
      setError(AUTH_MESSAGES.INVALID_PASSWORD);
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // The acceptance is recorded with the API on first sign-in (blocking legal dialog)
    if (!signupData.acceptTerms) {
      setError('You must accept the terms and conditions');
      return;
    }

    const sandbox = isSandboxBuild();
    if (sandbox && !signupData.fictionalDataConsent) {
      setError(SANDBOX_SIGNUP_MESSAGES.CONSENT_REQUIRED);
      return;
    }
    if (sandbox && !signupCaptchaToken) {
      setError('Complete the security check to continue.');
      return;
    }

    // Validate client and pool ID
    if (!cognitoClient || (!poolId && !orgId)) {
      setError('Authentication service not configured correctly');
      return;
    }

    // User agent for HIPAA auditing. No third-party IP lookup (Phase 2).
    const userAgent = navigator.userAgent;

    setIsLoading(true);

    try {
      // Register the user in Cognito. Confirming the email runs the PostConfirmation trigger
      // (createTenant), which creates the tenant and the owner user on a 14-day trial of the plan
      // chosen here (custom:subscription_tier: Starter or Core; the backend falls back to Core).
      await signUp({
        cognitoClient,
        username: email,
        password: signupData.password,
        clientId: appClientId || orgId || '',
        userAttributes: {
          given_name: signupData.firstName.trim(),
          family_name: signupData.lastName.trim(),
          email,
          organization_name: organizationName,
          subscription_tier: signupData.plan === 'starter' ? 'starter' : 'core',
          user_agent: userAgent
        },
        // Demo sandbox: every sign-up is a Core sandbox; consent and CAPTCHA go to PreSignUp only
        ...(sandbox && signupCaptchaToken
          ? { validationData: { fictionalDataConsent: 'yes', turnstileToken: signupCaptchaToken } }
          : {})
      });

      // Show email verification view
      setCurrentUser({ username: email, email });
      setVerificationCode('');
      setView('verify-email');
    } catch (err) {
      setError(sandboxSignupError(err) ?? errorMessage(err, 'Failed to sign up'));
    } finally {
      setIsLoading(false);
      if (sandbox) {
        // The token was spent: clear it and get a new one from the widget
        setSignupCaptchaToken(null);
        setSignupCaptchaResetKey((key) => key + 1);
      }
    }
  };

  /**
   * Email Verification Handler
   */
  const handleVerifyEmail = async (): Promise<void> => {
    if (!currentUser?.username || !verificationCode.trim()) {
      setError('Verification code is required');
      return;
    }

    setIsLoading(true);

    try {
      await verifyEmail({
        cognitoClient,
        username: currentUser.username,
        confirmationCode: verificationCode,
        clientId: appClientId || orgId || ''
      });
      // Confirmed: the PostConfirmation trigger has created the organization. Sign in next.
      setVerificationCode('');
      goToSignIn(currentUser.email, 'Email verified. Sign in to continue.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to verify email'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resend Verification Code Handler
   */
  const handleResendCode = async (): Promise<void> => {
    if (!currentUser?.username) {
      setError('User information is missing');
      return;
    }

    setIsLoading(true);

    try {
      await resendVerificationCode(cognitoClient, currentUser.username, appClientId || orgId || '');

      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Failed to resend verification code'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Forgot Password Handler. Rejects on every failure, so ForgotPassword moves to the
   * code step only after Cognito accepted the request.
   */
  const handleForgotPassword = async (): Promise<void> => {
    setError(null);
    const email = resetPasswordEmail.trim();
    const clientIdToUse = appClientId || orgId || '';
    const fail = (message: string): Error => {
      setError(message);
      return new Error(message);
    };

    if (!email) throw fail('Please enter your email address');
    if (!cognitoClient || !clientIdToUse) throw fail('Authentication service not configured correctly');

    setIsLoading(true);
    try {
      // An unknown email resolves like a real one (ACC-06: never reveal whether the account exists)
      await forgotPassword(cognitoClient, email, clientIdToUse);
      setResetPasswordEmail(email);
    } catch (err) {
      console.error('Forgot password request failed');
      throw fail(errorMessage(err, 'Unable to send a reset code right now. Please try again later.'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset Password Handler
   */
  const handleResetPassword = async (code: string, newPassword: string): Promise<void> => {
    setError(null);
    if (!resetPasswordEmail || !code || !newPassword) {
      setError('Please fill out all fields');
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError(AUTH_MESSAGES.INVALID_PASSWORD);
      return;
    }

    setIsLoading(true);

    try {
      await confirmForgotPassword({
        cognitoClient,
        username: resetPasswordEmail,
        confirmationCode: code,
        newPassword,
        clientId: appClientId || orgId || ''
      });
      goToSignIn(resetPasswordEmail, 'Your password has been reset. Sign in with your new password.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * MFA Setup Verification Handler.
   * VerifySoftwareToken runs against Cognito with the challenge session; the final
   * MFA_SETUP challenge response goes through POST /auth/challenge and signs the user in.
   */
  const handleVerifyMfaSetup = async (code: string): Promise<void> => {
    if (!currentUser?.username || !code) {
      setError('Verification code is required');
      return;
    }

    if (!currentUser?.session) {
      setError('Your session has expired. Please sign in again to set up MFA.');
      returnToSignIn(currentUser.email);
      return;
    }

    setIsLoading(true);

    try {
      const setupVerifyResult = await verifyMFA({
        cognitoClient,
        session: currentUser.session,
        code
      });
      if (!setupVerifyResult.success) {
        throw new Error('Invalid MFA code');
      }

      const challengeSession = setupVerifyResult.session || currentUser.session;
      try {
        const result = await AuthProxyService.respond({
          username: currentUser.username,
          challengeName: 'MFA_SETUP',
          session: challengeSession,
          responses: {}
        });
        await handleAuthResult(result, currentUser.email, currentUser.username);
      } catch (respondErr) {
        if (isSessionExpired(respondErr)) {
          // The authenticator is enrolled; signing in again now asks for the code
          goToSignIn(currentUser.email, 'MFA setup successful. Please sign in with your credentials and MFA code.');
        } else {
          // Lockout, rate limit or a server error: show it instead of claiming success
          console.error('MFA setup sign-in failed:', respondErr instanceof AuthProxyError ? respondErr.code : 'UNKNOWN');
          setError(respondErr instanceof AuthProxyError ? respondErr.message : AUTH_MESSAGES.UNKNOWN);
          returnToSignIn(currentUser.email, 1500);
        }
      }
    } catch (err) {
      console.error('MFA setup verification failed');

      if (isSessionExpired(err)) {
        setError('Your session has expired. Please sign in again to complete MFA setup.');
        returnToSignIn(currentUser.email);
      } else if (err instanceof Error && err.message.toLowerCase().includes('code')) {
        // Invalid verification code - let them try again
        setError('Invalid verification code. Please try again with a new code from your authenticator app.');
      } else {
        setError('There was a problem setting up MFA. Please sign in again.');
        returnToSignIn(currentUser.email);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * MFA Verification Handler (SOFTWARE_TOKEN_MFA through POST /auth/challenge)
   */
  const handleVerifyMfa = async (code: string): Promise<void> => {
    if (!currentUser?.username || !code) {
      setError('Verification code is required');
      return;
    }

    if (!currentUser?.session) {
      setError('Your session has expired. Please sign in again.');
      returnToSignIn(currentUser?.email);
      return;
    }

    setIsLoading(true);

    try {
      const result = await AuthProxyService.respond({
        username: currentUser.username,
        challengeName: 'SOFTWARE_TOKEN_MFA',
        session: currentUser.session,
        responses: { SOFTWARE_TOKEN_MFA_CODE: code }
      });
      await handleAuthResult(result, currentUser.email, currentUser.username);
    } catch (err) {
      console.error('MFA verification failed:', err instanceof AuthProxyError ? err.code : 'UNKNOWN');
      if (isSessionExpired(err)) {
        setError('Your session has expired. Please sign in again.');
        returnToSignIn(currentUser.email);
      } else if (err instanceof AuthProxyError && err.code === 'INVALID_CODE') {
        setError('Invalid MFA code. Please try again with a new code from your authenticator app.');
      } else {
        setError(errorMessage(err, 'Failed to verify MFA code'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle New Password Submission (NEW_PASSWORD_REQUIRED through POST /auth/challenge)
   */
  const handleNewPassword = async (newPassword: string): Promise<void> => {
    if (!currentUser?.username || !currentUser?.session) {
      setError('Your session has expired. Please sign in again.');
      returnToSignIn();
      return;
    }

    setIsLoading(true);

    try {
      const result = await AuthProxyService.respond({
        username: currentUser.username,
        challengeName: 'NEW_PASSWORD_REQUIRED',
        session: currentUser.session,
        responses: {
          NEW_PASSWORD: newPassword,
          // middle_name is a required pool attribute; invited users never set it
          'userAttributes.middle_name': 'NA'
        }
      });
      await handleAuthResult(result, currentUser.email, currentUser.username);
    } catch (err) {
      console.error('New password challenge failed:', err instanceof AuthProxyError ? err.code : 'UNKNOWN');
      if (err instanceof AuthProxyError && err.code === 'INVALID_PASSWORD') {
        setError(err.message);
      } else if (isSessionExpired(err)) {
        setError('Your session has expired. Please sign in again.');
        returnToSignIn();
      } else {
        setError(errorMessage(err, 'Failed to set new password'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render organization ID input for multi-tenant apps
  const renderOrgIdInput = () => {
    // Only show organization ID input if userPoolId is not provided
    if (userPoolId) {
      return null;
    }
    
    return (
      <div className="flex flex-col gap-2">
        <label className="font-medium text-slate-800 text-sm text-left" htmlFor="orgId">Organization ID</label>
        <input
          type="text"
          id="orgId"
          className="w-full p-3 border border-slate-200 rounded-md text-base transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
          value={orgId}
          onChange={e => setOrgId(e.target.value)}
          placeholder="Enter your organization ID"
        />
      </div>
    );
  };
  // Render the appropriate view
  const renderView = () => {
    const backToSignIn = () => goToSignIn(currentUser?.email);

    switch (view) {
      case 'signin':
        return (
          <SignInForm
            signinData={signinData}
            handleSigninChange={handleSigninChange}
            handleSignin={handleSignin}
            handleForgotPassword={() => { setResetPasswordEmail(signinData.email.trim()); setView('forgot-password'); }}
            setView={(viewName) => setView(viewName as ViewState)}
            error={error}
            notice={notice}
            isLoading={isLoading}
            isLoadingPermissions={waitingForPermissions && permissionsLoading}
            renderOrgIdInput={userPoolId ? undefined : renderOrgIdInput}
            captchaRequired={captchaRequired}
            captchaToken={captchaToken}
            onCaptchaToken={setCaptchaToken}
            captchaResetKey={captchaResetKey}
          />
        );
        
      case 'signup':
        return (
          <SignUpForm
            signupData={signupData}
            handleSignupChange={handleSignupChange}
            handleSignup={handleSignup}
            setView={(viewName) => setView(viewName as ViewState)}
            error={error}
            isLoading={isLoading}
            onCaptchaToken={setSignupCaptchaToken}
            captchaResetKey={signupCaptchaResetKey}
            hasCaptchaToken={Boolean(signupCaptchaToken)}
          />
        );
        
      case 'verify-email':
        return (
          <EmailVerification
            email={currentUser?.email || ''}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            handleVerifyEmail={handleVerifyEmail}
            handleResendCode={handleResendCode}
            onBack={backToSignIn}
            error={error}
            isLoading={isLoading}
          />
        );
        
      case 'forgot-password':
        return (
          <ForgotPassword
            email={resetPasswordEmail}
            setEmail={setResetPasswordEmail}
            handleForgotPassword={handleForgotPassword}
            handleResetPassword={handleResetPassword}
            setView={(viewName) => (viewName === 'signin' ? goToSignIn(resetPasswordEmail.trim()) : setView(viewName as ViewState))}
            error={error}
            isLoading={isLoading}
          />
        );
          case 'mfa-setup':
        return (
          <MfaSetup
            qrCodeUrl={qrCodeUrl}
            handleVerifyMfaSetup={handleVerifyMfaSetup}
            error={error}
            isLoading={isLoading}
          />
        );
          case 'mfa-verify':
        return (
          <MfaVerify
            handleVerifyMfa={handleVerifyMfa}
            error={error}
            isLoading={isLoading}
            onCancel={backToSignIn}
          />
        );
        
      case 'new-password':
        return (
          <NewPasswordForm
            username={currentUser?.email || ''}
            handleNewPasswordSubmit={handleNewPassword}
            error={error}
            isLoading={isLoading}
            onCancel={backToSignIn}
          />
        );
        
      default:
        return <div>Unknown view</div>;
    }
  };    return (    <div className="md:flex md:flex-row md:h-screen md:overflow-hidden min-h-screen w-full">
      {/* Banner section - shown at top on mobile, left side on desktop - sticky in both modes */}
      <div className="z-10 bg-slate-900 w-full md:w-1/2 md:h-screen md:sticky md:top-0 flex items-center justify-center py-5 px-6 md:p-10 md:flex-col" style={isMobile ? { position: 'fixed', top: 0, left: 0, right: 0, WebkitPosition: 'fixed' } as React.CSSProperties : undefined}>
        <div className="w-full max-w-md text-left">
          <div className="flex items-center mb-4 md:mb-10">
            {view === 'signup' ? (
              <>
                <img src={iconWhite} alt="Pelora Health" className="h-8 md:h-10 mr-3" />
                <span className="text-white text-lg md:text-2xl font-semibold">Pelora Health</span>
              </>
            ) : (
              <>
                <img src={iconWhite} alt="Pelora Health" className="h-8 md:h-10 mr-3" />
                <span className="text-white text-lg md:text-2xl font-semibold">Pelora Health</span>
              </>
            )}
          </div>
          <h1 className="text-xl md:text-4xl font-bold text-white mb-3 md:mb-6">
            Streamline your healthcare operations
          </h1>
          <p className="text-white/80 text-sm md:text-lg mb-1">
            A powerful, budget-friendly EMR that streamlines documentation, billing, and compliance—without breaking the bank.
          </p>
          {!isMobile && (
          <div className="mt-8 md:mt-12 border-t border-white/20 pt-6 md:pt-8">
            <div className="flex gap-3 md:gap-4">
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400 md:w-6 md:h-6">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div>
                <h3 className="text-white font-medium text-sm md:text-base">Complete EMR for Home Health</h3>
                <p className="text-white/70 text-xs md:text-sm mt-1">Full-featured EMR built specifically for home health agencies</p>
              </div>
            </div>
            <div className="flex gap-3 md:gap-4 mt-4 md:mt-6">
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-400 md:w-6 md:h-6">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-white font-medium text-sm md:text-base">Affordable Pricing, Zero Compromises</h3>
                <p className="text-white/70 text-xs md:text-sm mt-1">Advanced features at a fraction of the cost</p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
      
      {/* Right column - authentication form - scrollable content. `relative` makes it the
          containing block for absolutely positioned descendants (e.g. sr-only labels in the sign-up
          form); without it they anchor to the page, stretch it past the window, and the whole page
          (hero included) scrolls instead of only the form. */}
      <div className={`relative bg-white w-full md:w-1/2 md:h-screen md:min-h-0 flex justify-center overflow-y-auto min-h-screen ${isMobile ? 'items-start pt-4 px-4 pb-6' : 'items-start p-8'}`} style={isMobile ? { marginTop: 'auto', paddingTop: '185px' } : {}}>
        <div className="w-full flex flex-col items-center justify-center md:justify-start">
          {renderView()}
          {/* Phase 16A: phones and tablets can install pelora for offline use (hidden on computers) */}
          {view === 'signin' && (
            <div className="mt-6 w-full max-w-md">
              <InstallAppButton />
              <p className="mt-3 text-center">
                <Link to="/install" className="inline-flex min-h-[44px] items-center text-sm font-medium text-sky-700 underline">
                  How to install pelora on your phone or tablet
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthenticationComponent;
